import { getDatabase, ref, push, set, get } from 'firebase/database';
import { getAuth } from 'firebase/auth';

// Cache for logger config to avoid repeated DB calls
let loggerConfigCache: 'error' | 'info' | null = null;
let loggerConfigPromise: Promise<'error' | 'info' | null> | null = null;

/**
 * Get logger config from database
 * Returns: 'error' | 'info' | null
 * - 'error' - writes both error and info logs
 * - 'info' - writes only info logs
 * - null - writes nothing
 * Uses caching to avoid repeated database calls
 * Directly accesses Firebase to avoid circular dependencies with firebaseApi
 */
async function getLoggerConfig(): Promise<'error' | 'info' | null> {
  // If we have a cached value, return it
  if (loggerConfigCache !== null) {
    return loggerConfigCache;
  }

  // If there's already a pending request, return that promise
  if (loggerConfigPromise) {
    return loggerConfigPromise;
  }

  // Create a new promise to fetch the config
  loggerConfigPromise = (async () => {
    try {
      const db = getDatabase();
      const configSnap = await get(ref(db, 'Config'));
      const config = configSnap.val();
      const loggerValue = config?.logger;
      
      // Handle string values 'error' or 'info', or null/undefined
      if (loggerValue === 'error' || loggerValue === 'info') {
        loggerConfigCache = loggerValue;
        return loggerValue;
      }
      
      // Default to null if not set or invalid
      loggerConfigCache = null;
      return null;
    } catch (error) {
      // On error, default to null (no logging)
      loggerConfigCache = null;
      return null;
    } finally {
      // Clear the promise after completion
      loggerConfigPromise = null;
    }
  })();

  return loggerConfigPromise;
}

// Audit log types
export enum AuditAction {
  CREATE = 'CREATE',
  READ = 'READ', 
  UPDATE = 'UPDATE',
  DELETE = 'DELETE',
  LOGIN = 'LOGIN',
  LOGOUT = 'LOGOUT',
  LOGIN_ATTEMPT = 'LOGIN_ATTEMPT',
  ACCESS_DENIED = 'ACCESS_DENIED'
}

export enum AuditResource {
  BOOKING = 'booking',
  STATION = 'station', 
  STATION_TYPE = 'stationType',
  USER = 'user',
  AUTH = 'auth'
}

export interface AuditLogEntry {
  timestamp: string;
  action: AuditAction;
  resource: AuditResource;
  resourceId?: string;
  userId?: string;
  userEmail?: string;
  userName?: string;
  details?: any;
  success: boolean;
  errorMessage?: string;
  ipAddress?: string;
  userAgent?: string;
  sessionId?: string;
}

// Helper to get user context
function getUserContext() {
  const auth = getAuth();
  const user = auth.currentUser;
  
  return {
    userId: user?.uid,
    userEmail: user?.email,
    userName: user?.displayName
  };
}

// Helper to get browser context
function getBrowserContext() {
  return {
    userAgent: navigator.userAgent,
    // IP address would need to be obtained from server-side or external service
    // For now we'll leave it undefined
    ipAddress: undefined
  };
}

// Helper function to remove undefined values from an object
function removeUndefinedValues(obj: any): any {
  const cleaned: any = {};
  Object.keys(obj).forEach(key => {
    if (obj[key] !== undefined) {
      cleaned[key] = obj[key];
    }
  });
  return cleaned;
}

// Main audit logging function
export async function logAuditEvent(
  action: AuditAction,
  resource: AuditResource,
  success: boolean = true,
  options: {
    resourceId?: string;
    details?: any;
    errorMessage?: string;
    customUserId?: string;
    customUserEmail?: string;
    customUserName?: string;
    force?: boolean; // If true, always write regardless of config (for important audit logs)
  } = {}
): Promise<void> {
  try {
    const userContext = getUserContext();
    const browserContext = getBrowserContext();
    
    const auditEntry: AuditLogEntry = {
      timestamp: new Date().toISOString(),
      action,
      resource,
      resourceId: options.resourceId,
      userId: options.customUserId || userContext.userId || undefined,
      userEmail: options.customUserEmail || userContext.userEmail || undefined,
      userName: options.customUserName || userContext.userName || undefined,
      details: options.details,
      success,
      errorMessage: options.errorMessage,
      ipAddress: browserContext.ipAddress,
      userAgent: browserContext.userAgent,
      sessionId: generateSessionId()
    };

    // If force is true, always write (for important audit logs)
    // Otherwise, check if logger is enabled in config
    if (!options.force) {
      const loggerConfig = await getLoggerConfig();
      
      if (loggerConfig === null) {
        // Logger is disabled, don't write to database
        return;
      }
    }

    // Remove undefined values to prevent Firebase errors
    const cleanedAuditEntry = removeUndefinedValues(auditEntry);

    // Save to Firebase "log" collection
    const db = getDatabase();
    const logRef = push(ref(db, 'log'));
    await set(logRef, cleanedAuditEntry);
    
    // Also log to console for development
    console.log('Audit Log:', cleanedAuditEntry);
    
  } catch (error) {
    // Never throw errors from audit logging to avoid disrupting main application flow
    console.error('Failed to write audit log:', error);
    console.error('Audit log error details:', {
      action,
      resource,
      success,
      force: options.force,
      error: error instanceof Error ? error.message : String(error)
    });
  }
}

// Generate a session ID (simple implementation)
function generateSessionId(): string {
  // Use a combination of timestamp and random number
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

// Regular logging types
export interface RegularLogEntry {
  timestamp: string;
  level: 'error' | 'info';
  message: string;
  details?: any;
  userId?: string;
  userEmail?: string;
  userName?: string;
  sessionId?: string;
}

/**
 * Log an error event (writes to 'log' collection)
 * Only writes if Config/logger is 'error' or 'info'
 */
export async function logError(message: string, details?: any): Promise<void> {
  try {
    const loggerConfig = await getLoggerConfig();
    
    // Write if config is 'error' or 'info' (not null)
    if (loggerConfig !== 'error' && loggerConfig !== 'info') {
      return;
    }

    const userContext = getUserContext();
    const logEntry: RegularLogEntry = {
      timestamp: new Date().toISOString(),
      level: 'error',
      message,
      details,
      userId: userContext.userId || undefined,
      userEmail: userContext.userEmail || undefined,
      userName: userContext.userName || undefined,
      sessionId: generateSessionId()
    };

    const cleanedEntry = removeUndefinedValues(logEntry);

    const db = getDatabase();
    const logRef = push(ref(db, 'log'));
    await set(logRef, cleanedEntry);
    
    console.error('Error Log:', cleanedEntry);
  } catch (error) {
    // Never throw errors from logging
    console.error('Failed to write error log:', error);
  }
}

/**
 * Log an info event (writes to 'log' collection)
 * Only writes if Config/logger is 'info' (writes both info and error when 'info')
 */
export async function logInfo(message: string, details?: any): Promise<void> {
  try {
    const loggerConfig = await getLoggerConfig();
    
    // Only write if config is 'info' (not 'error' or null)
    if (loggerConfig !== 'info') {
      return;
    }

    const userContext = getUserContext();
    const logEntry: RegularLogEntry = {
      timestamp: new Date().toISOString(),
      level: 'info',
      message,
      details,
      userId: userContext.userId || undefined,
      userEmail: userContext.userEmail || undefined,
      userName: userContext.userName || undefined,
      sessionId: generateSessionId()
    };

    const cleanedEntry = removeUndefinedValues(logEntry);

    const db = getDatabase();
    const logRef = push(ref(db, 'log'));
    await set(logRef, cleanedEntry);
    
    console.log('Info Log:', cleanedEntry);
  } catch (error) {
    // Never throw errors from logging
    console.error('Failed to write info log:', error);
  }
}

// Convenience functions for common audit events
export const auditLog = {
  // Authentication events
  loginSuccess: (userEmail: string, userName?: string) =>
    logInfo('User logged in', {
      userEmail,
      userName,
      method: 'google'
    }),
    
  loginFailure: (errorMessage: string, userEmail?: string) =>
    logError('Login failed', {
      errorMessage,
      userEmail
    }),
    
  logout: () =>
    logInfo('User logged out'),

  // Booking events  
  bookingCreated: (bookingId: string, bookingData: any) =>
    logInfo('Booking created', {
      bookingId,
      bookingData
    }),
    
  bookingCreationFailed: (errorMessage: string, bookingData: any) =>
    logError('Booking creation failed', {
      errorMessage,
      bookingData
    }),
    
  bookingsRead: (count: number) =>
    logInfo('Bookings read', {
      count
    }),
    
  bookingRead: (bookingId: string) =>
    logInfo('Booking read', {
      bookingId
    }),

  // Station events
  stationsRead: (count: number) =>
    logInfo('Stations read', {
      count
    }),
    
  stationTypesRead: (count: number) =>
    logInfo('Station types read', {
      count
    }),
    
  stationBookingCheck: (stationId: string, fromDate: string, toDate: string, isBooked: boolean) =>
    logInfo('Station booking check', {
      stationId,
      fromDate,
      toDate,
      isBooked
    }),

  // Generic record operations
  recordCreated: (collection: string, recordId: string, data: any) =>
    logInfo(`Record created: ${collection}`, {
      recordId,
      collection,
      data
    }),
    
  recordCreationFailed: (collection: string, errorMessage: string, data: any) =>
    logError(`Record creation failed: ${collection}`, {
      errorMessage,
      collection,
      data
    }),

  recordUpdated: (collection: string, recordId: string, data: any) =>
    logInfo(`Record updated: ${collection}`, {
      recordId,
      collection,
      data
    }),
    
  recordUpdateFailed: (collection: string, recordId: string, errorMessage: string) =>
    logError(`Record update failed: ${collection}`, {
      recordId,
      errorMessage,
      collection
    }),

  recordDeleted: (collection: string, recordId: string, data: any) =>
    logInfo(`Record deleted: ${collection}`, {
      recordId,
      collection,
      data
    }),

  // Important audit events (always written, regardless of config)
  bookingRejected: async (bookingId: string, bookingData: any) => {
    console.log('bookingRejected called with:', { bookingId, bookingData: !!bookingData });
    await logAuditEvent(AuditAction.DELETE, AuditResource.BOOKING, true, {
      resourceId: bookingId,
      details: bookingData,
      force: true // Always log reject booking
    });
  },

  // Access control events
  accessDenied: (resource: AuditResource, reason: string) =>
    logError('Access denied', {
      resource,
      reason
    })
};

/**
 * Clear the logger config cache (useful when config changes)
 * This will force the next audit log to re-fetch the config from the database
 */
export function clearLoggerConfigCache(): void {
  loggerConfigCache = null;
  loggerConfigPromise = null;
}
